import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { AlertCircle, Camera, ChevronLeft, ChevronRight } from 'lucide-react';

interface PreviewProps {
  templateData: string;
  variables: Record<string, any>;
  errors?: Record<string, string>;
}

// React Error Boundary for catching unhandled errors
class WhatsAppPreviewErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('WhatsApp Preview Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            padding: '20px',
            backgroundColor: '#ffebee',
            border: '1px solid #ffcdd2',
            borderRadius: '8px',
            textAlign: 'center',
            maxWidth: '320px',
            margin: '0 auto'
          }}
        >
          <AlertCircle style={{ width: '24px', height: '24px', color: '#d32f2f', marginBottom: '8px' }} />
          <Typography sx={{ fontSize: '14px', color: '#d32f2f', marginBottom: '8px' }}>
            WhatsApp Preview Error
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
            {this.state.error?.message || 'An unexpected error occurred while rendering the preview'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => this.setState({ hasError: false, error: null })}
            sx={{ fontSize: '12px' }}
          >
            Reset Preview
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Error boundary component for internal error handling
const ErrorFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => (
  <Box
    sx={{
      padding: '20px',
      backgroundColor: '#ffebee',
      border: '1px solid #ffcdd2',
      borderRadius: '8px',
      textAlign: 'center'
    }}
  >
    <AlertCircle style={{ width: '24px', height: '24px', color: '#d32f2f', marginBottom: '8px' }} />
    <Typography sx={{ fontSize: '14px', color: '#d32f2f', marginBottom: '8px' }}>
      Preview temporarily unavailable
    </Typography>
    <Typography sx={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
      {error.message || 'An unexpected error occurred'}
    </Typography>
    <Button
      size="small"
      variant="outlined"
      onClick={resetError}
      sx={{ fontSize: '12px' }}
    >
      Retry
    </Button>
  </Box>
);

const WhatsAppPreview = ({ templateData, variables = {}, errors = {} }: PreviewProps) => {
  const [imagePreviews, setImagePreviews] = useState<Record<string, string>>({});
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [componentError, setComponentError] = useState<Error | null>(null);

  // Helper function to check if a value is a valid media source
  const isValidMediaSource = (value: any, mediaType: 'image' | 'video'): boolean => {
    try {
      if (value instanceof File) {
        if (mediaType === 'image') {
          return value.type.startsWith('image/');
        } else if (mediaType === 'video') {
          return value.type.startsWith('video/');
        }
      } else if (typeof value === 'string') {
        return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:');
      }
      return false;
    } catch (error) {
      console.warn('Error checking media source:', error);
      return false;
    }
  };

  // Helper function to get media source URL
  const getMediaSourceUrl = (value: any, previewUrl?: string): string | null => {
    try {
      if (previewUrl) return previewUrl;
      if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:'))) {
        return value;
      }
      return null;
    } catch (error) {
      console.warn('Error getting media source URL:', error);
      return null;
    }
  };

  // Input validation and sanitization
  const safeTemplateData = useMemo(() => {
    try {
      if (!templateData) return '';
      if (typeof templateData !== 'string') {
        console.warn('Template data is not a string, converting:', typeof templateData);
        return String(templateData);
      }
      return templateData;
    } catch (error) {
      console.warn('Error processing template data:', error);
      return '';
    }
  }, [templateData]);

  const safeVariables = useMemo(() => {
    try {
      if (!variables || typeof variables !== 'object') {
        console.warn('Variables is not an object, using empty object');
        return {};
      }
      // Filter out any non-serializable values
      const filtered: Record<string, any> = {};
      Object.entries(variables).forEach(([key, value]) => {
        try {
          if (key && (typeof value === 'string' || typeof value === 'number' || value instanceof File || value === null || value === undefined)) {
            filtered[key] = value;
          }
        } catch (error) {
          console.warn(`Error processing variable ${key}:`, error);
        }
      });
      return filtered;
    } catch (error) {
      console.warn('Error processing variables:', error);
      return {};
    }
  }, [variables]);

  const safeErrors = useMemo(() => {
    try {
      if (!errors || typeof errors !== 'object') {
        return {};
      }
      // Ensure all error values are strings
      const filtered: Record<string, string> = {};
      Object.entries(errors).forEach(([key, value]) => {
        try {
          if (key && value) {
            filtered[key] = String(value);
          }
        } catch (error) {
          console.warn(`Error processing error ${key}:`, error);
        }
      });
      return filtered;
    } catch (error) {
      console.warn('Error processing errors:', error);
      return {};
    }
  }, [errors]);

  // Safe template parsing with error handling
  const getParsedTemplate = useMemo(() => {
    try {
      if (!safeTemplateData || typeof safeTemplateData !== 'string') {
        return [];
      }
      const parsed = JSON.parse(safeTemplateData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to parse template data:', error);
      return [];
    }
  }, [safeTemplateData]);

  // Safe carousel detection
  const isCarousel = useMemo(() => {
    try {
      return getParsedTemplate.some((section: any) =>
        section && typeof section === 'object' && section.type === 'CAROUSEL'
      );
    } catch (error) {
      console.warn('Error detecting carousel:', error);
      return false;
    }
  }, [getParsedTemplate]);

  // Safe file processing with comprehensive error handling
  useEffect(() => {
    const processFiles = async () => {
      try {
        if (!safeVariables || typeof safeVariables !== 'object') {
          return;
        }

        const readers: FileReader[] = [];

        // Clear previous previews for removed variables
        setImagePreviews(prev => {
          try {
            const updatedPreviews: Record<string, string> = {};
            Object.keys(prev).forEach(key => {
              // Keep existing URL previews and File previews that still exist
              if (safeVariables[key] instanceof File || (typeof safeVariables[key] === 'string' && safeVariables[key].startsWith('http'))) {
                updatedPreviews[key] = prev[key];
              }
            });
            return updatedPreviews;
          } catch (error) {
            console.warn('Error clearing previews:', error);
            return {};
          }
        });

        // Process all media files and URLs
        Object.entries(safeVariables).forEach(([key, value]) => {
          try {
            if ((key.includes('header_image') || key.includes('_image') ||
              key.includes('header_video') || key.includes('_video'))) {

              // Handle File objects (new uploads)
              if (value instanceof File) {
                // Validate file size (max 50MB)
                if (value.size > 50 * 1024 * 1024) {
                  console.warn(`File ${key} is too large:`, value.size);
                  return;
                }

                // Validate file type
                const isImage = key.includes('image') && isValidMediaSource(value, 'image');
                const isVideo = key.includes('video') && isValidMediaSource(value, 'video');

                if (!isImage && !isVideo) {
                  console.warn(`Invalid file type for ${key}:`, value.type);
                  return;
                }

                const reader = new FileReader();
                readers.push(reader);

                reader.onloadend = () => {
                  try {
                    if (reader.result && typeof reader.result === 'string') {
                      setImagePreviews(prev => ({
                        ...prev,
                        [key]: reader.result as string
                      }));
                    }
                  } catch (error) {
                    console.warn(`Error setting preview for ${key}:`, error);
                  }
                };

                reader.onerror = () => {
                  console.warn(`Failed to read file for ${key}`);
                };

                reader.readAsDataURL(value);
              }
              // Handle URL strings (existing media from API)
              else if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:'))) {
                setImagePreviews(prev => ({
                  ...prev,
                  [key]: value
                }));
              }
            }
          } catch (error) {
            console.warn(`Error processing media ${key}:`, error);
          }
        });

        // Cleanup function
        return () => {
          readers.forEach(reader => {
            try {
              if (reader.readyState === FileReader.LOADING) {
                reader.abort();
              }
            } catch (error) {
              console.warn('Error aborting file reader:', error);
            }
          });
        };
      } catch (error) {
        console.warn('Error in file processing:', error);
      }
    };

    processFiles();
  }, [safeVariables]);

  // Safe card index reset
  useEffect(() => {
    try {
      setCurrentCardIndex(0);
    } catch (error) {
      console.warn('Error resetting card index:', error);
    }
  }, [safeTemplateData]);

  // Early return for completely invalid data (after all hooks)
  if (!safeTemplateData && Object.keys(safeVariables).length === 0) {
    return (
      <Box sx={{ maxWidth: '320px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <Typography sx={{ fontSize: '14px', color: '#8696a0' }}>
          No template data available
        </Typography>
      </Box>
    );
  }

  // Safe header rendering with comprehensive error handling
  const renderHeader = (header: any, cardIndex?: number) => {
    try {
      if (!header || typeof header !== 'object') return null;

      const format = header.format;
      if (!format || typeof format !== 'string') return null;

      // Handle image preview
      if (format === 'IMAGE') {
        const imageKey = cardIndex !== undefined
          ? `carousel_cards_${cardIndex}_header_image`
          : 'header_image';

        const imageValue = safeVariables?.[imageKey];
        const imagePreview = imagePreviews?.[imageKey];
        const imageSourceUrl = getMediaSourceUrl(imageValue, imagePreview);
        const hasValidImageSource = isValidMediaSource(imageValue, 'image');

        return (
          <Box sx={{ position: 'relative' }} key={`header-${imageKey}`}>
            {imageSourceUrl ? (
              <Box
                sx={{
                  width: '100%',
                  height: '160px',
                  minWidth: '250px',
                  position: 'relative',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={imageSourceUrl}
                  alt="Header"
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    console.warn('Image load error for:', imageSourceUrl);
                    // Hide broken image or show placeholder
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minWidth: '250px',
                  height: '160px',
                  backgroundColor: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  flexDirection: 'column'
                }}
              >
                <Camera style={{ width: '40px', height: '40px', color: '#6b7280' }} />
                {hasValidImageSource && (
                  <Typography sx={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                    Loading image...
                  </Typography>
                )}
              </Box>
            )}
            {safeErrors?.header && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  color: 'white',
                  padding: '6px 8px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <AlertCircle style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                {String(safeErrors.header)}
              </Box>
            )}
          </Box>
        );
      }

      // Handle video preview
      if (format === 'VIDEO') {
        const videoKey = cardIndex !== undefined
          ? `carousel_cards_${cardIndex}_header_video`
          : 'header_video';

        const videoValue = safeVariables?.[videoKey];
        const videoPreview = imagePreviews?.[videoKey];
        const videoSourceUrl = getMediaSourceUrl(videoValue, videoPreview);
        const hasValidVideoSource = isValidMediaSource(videoValue, 'video');

        return (
          <Box sx={{ position: 'relative' }} key={`header-${videoKey}`}>
            {videoSourceUrl ? (
              <Box
                sx={{
                  width: '100%',
                  height: '160px',
                  minWidth: '250px',
                  position: 'relative',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <video
                  src={videoSourceUrl}
                  controls
                  onError={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
                    console.warn('Video load error for:', videoSourceUrl);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundColor: '#000'
                  }}
                />
                {/* Video play overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    pointerEvents: 'none'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minWidth: '250px',
                  height: '160px',
                  backgroundColor: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ color: '#6b7280', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z" />
                  </svg>
                  {hasValidVideoSource && (
                    <Typography sx={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                      Loading video...
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {safeErrors?.header && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  color: 'white',
                  padding: '6px 8px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <AlertCircle style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                {String(safeErrors.header)}
              </Box>
            )}
          </Box>
        );
      }

      // Handle text header
      if (format === 'TEXT' && header.text) {
        const headerKey = cardIndex !== undefined
          ? `carousel_cards_${cardIndex}_header_text`
          : 'header_text';

        const headerValue = safeVariables?.[headerKey] || header.text || '';

        return (
          <Box sx={{ padding: '12px 12px 8px' }} key={`header-text-${headerKey}`}>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#e9edef',
                lineHeight: 1.3
              }}
            >
              {String(headerValue)}
            </Typography>
          </Box>
        );
      }

      return null;
    } catch (error) {
      console.warn('Error rendering header:', error);
      return (
        <Box sx={{ padding: '12px', textAlign: 'center', color: '#8696a0' }}>
          <Typography sx={{ fontSize: '12px' }}>Header preview unavailable</Typography>
        </Box>
      );
    }
  };

  // Safe variable replacement with comprehensive error handling
  const replaceVariables = (text: string, exampleVars: any, cardIndex?: number) => {
    try {
      if (!text || typeof text !== 'string') return '';

      let processedText = text;

      if (cardIndex !== undefined) {
        // Handle carousel card variables - more comprehensive replacement
        if (safeVariables && typeof safeVariables === 'object') {
          Object.entries(safeVariables).forEach(([key, value]) => {
            try {
              if (key.startsWith(`carousel_cards_${cardIndex}_`)) {
                const variableType = key.split('_').slice(3).join('_');

                // Handle different variable patterns
                if (variableType === 'body' && value) {
                  processedText = processedText.replace(/\{\{1\}\}/, String(value));
                }

                // Handle other numbered variables like {{2}}, {{3}}, etc.
                const match = variableType.match(/^body_(\d+)$/);
                if (match && value) {
                  const variableNumber = match[1];
                  const pattern = new RegExp(`\\{\\{${variableNumber}\\}\\}`, 'g');
                  processedText = processedText.replace(pattern, String(value));
                }
              }
            } catch (error) {
              console.warn(`Error processing variable ${key}:`, error);
            }
          });
        }
      } else {
        // Handle regular template variables
        try {
          if (exampleVars?.body_text?.[0] && Array.isArray(exampleVars.body_text[0])) {
            exampleVars.body_text[0].forEach((value: string, index: number) => {
              try {
                const varName = `body_${index + 1}`;
                const variableValue = safeVariables?.[varName] || value || `{{${index + 1}}}`;
                processedText = processedText.replace(`{{${index + 1}}}`, String(variableValue));
              } catch (error) {
                console.warn(`Error replacing variable at index ${index}:`, error);
              }
            });
          }

          // Also handle direct variable replacement
          if (safeVariables && typeof safeVariables === 'object') {
            Object.entries(safeVariables).forEach(([key, value]) => {
              try {
                if (key.startsWith('body_') && value && typeof value === 'string') {
                  const match = key.match(/^body_(\d+)$/);
                  if (match) {
                    const variableNumber = match[1];
                    const pattern = new RegExp(`\\{\\{${variableNumber}\\}\\}`, 'g');
                    processedText = processedText.replace(pattern, value);
                  }
                }
              } catch (error) {
                console.warn(`Error processing direct variable ${key}:`, error);
              }
            });
          }
        } catch (error) {
          console.warn('Error in regular variable replacement:', error);
        }
      }

      return processedText;
    } catch (error) {
      console.warn('Error in replaceVariables:', error);
      return text || '';
    }
  };

  // Safe carousel card rendering
  const renderCarouselCard = (card: any, cardIndex: number) => {
    try {
      if (!card || typeof card !== 'object') {
        return (
          <Box sx={{ padding: '20px', textAlign: 'center', color: '#8696a0' }}>
            <Typography sx={{ fontSize: '14px' }}>Card data unavailable</Typography>
          </Box>
        );
      }

      const headerSection = card.components?.find((comp: any) =>
        comp && typeof comp === 'object' && comp.type === 'HEADER'
      );
      const bodySection = card.components?.find((comp: any) =>
        comp && typeof comp === 'object' && comp.type === 'BODY'
      );
      const buttonSection = card.components?.find((comp: any) =>
        comp && typeof comp === 'object' && comp.type === 'BUTTONS'
      );

      return (
        <Box
          key={`carousel-card-${cardIndex}`}
          sx={{
            backgroundColor: '#005c4b',
            borderRadius: '8px',
            overflow: 'hidden',
            minHeight: '200px',
            position: 'relative'
          }}
        >
          {/* Header Media */}
          {renderHeader(headerSection, cardIndex)}

          {/* Message Content */}
          <Box sx={{ padding: headerSection ? '8px 12px 12px' : '12px' }}>
            {bodySection && (
              <Box sx={{ position: 'relative' }} key={`carousel-body-${cardIndex}`}>
                <Typography
                  sx={{
                    fontSize: '14.2px',
                    color: '#e9edef',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.4,
                    wordBreak: 'break-word'
                  }}
                >
                  {replaceVariables(bodySection.text || '', bodySection.example, cardIndex)}
                </Typography>
                {safeErrors?.body && (
                  <Box
                    sx={{
                      marginTop: '8px',
                      color: '#ff6b6b',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <AlertCircle style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                    {String(safeErrors.body)}
                  </Box>
                )}
              </Box>
            )}

            {/* Buttons */}
            {buttonSection?.buttons && Array.isArray(buttonSection.buttons) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  marginTop: '12px',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  paddingTop: '12px'
                }}
              >
                {buttonSection.buttons.map((button: any, index: number) => {
                  try {
                    return (
                      <Button
                        key={`carousel-button-${cardIndex}-${index}`}
                        sx={{
                          width: '100%',
                          padding: '8px 12px',
                          textAlign: 'center',
                          backgroundColor: 'transparent',
                          border: '1px solid rgba(255,255,255,0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.1)'
                          },
                          color: '#53bdeb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 500,
                          textTransform: 'none',
                          minHeight: 'auto'
                        }}
                      >
                        {String(button?.text || `Button ${index + 1}`)}
                      </Button>
                    );
                  } catch (error) {
                    console.warn(`Error rendering button ${index}:`, error);
                    return null;
                  }
                })}
              </Box>
            )}
          </Box>
        </Box>
      );
    } catch (error) {
      console.warn('Error rendering carousel card:', error);
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#ff6b6b' }}>
          <Typography sx={{ fontSize: '14px' }}>Card preview unavailable</Typography>
        </Box>
      );
    }
  };

  // Safe regular template rendering
  const renderRegularTemplate = () => {
    try {
      const headerSection = getParsedTemplate.find((section: any) =>
        section && typeof section === 'object' && section.type === 'HEADER'
      );
      const bodySection = getParsedTemplate.find((section: any) =>
        section && typeof section === 'object' && section.type === 'BODY'
      );
      const buttonSection = getParsedTemplate.find((section: any) =>
        section && typeof section === 'object' && section.type === 'BUTTONS'
      );

      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Box
            sx={{
              maxWidth: '280px',
              backgroundColor: '#005c4b',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Header Media */}
            {renderHeader(headerSection)}

            {/* Message Content */}
            <Box sx={{ padding: headerSection ? '8px 12px 12px' : '12px' }}>
              {bodySection && (
                <Box sx={{ position: 'relative' }} key="regular-body">
                  <Typography
                    sx={{
                      fontSize: '14.2px',
                      color: '#e9edef',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.4,
                      wordBreak: 'break-word'
                    }}
                  >
                    {replaceVariables(bodySection.text || '', bodySection.example)}
                  </Typography>
                  {safeErrors?.body && (
                    <Box
                      sx={{
                        marginTop: '8px',
                        color: '#ff6b6b',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <AlertCircle style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                      {String(safeErrors.body)}
                    </Box>
                  )}
                </Box>
              )}

              {/* Buttons */}
              {buttonSection?.buttons && Array.isArray(buttonSection.buttons) && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    marginTop: '12px',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    paddingTop: '12px'
                  }}
                >
                  {buttonSection.buttons.map((button: any, index: number) => {
                    try {
                      return (
                        <Button
                          key={`regular-button-${index}`}
                          sx={{
                            width: '100%',
                            padding: '8px 12px',
                            textAlign: 'center',
                            backgroundColor: 'transparent',
                            border: '1px solid rgba(255,255,255,0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.1)'
                            },
                            color: '#53bdeb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                            minHeight: 'auto'
                          }}
                        >
                          {String(button?.text || `Button ${index + 1}`)}
                        </Button>
                      );
                    } catch (error) {
                      console.warn(`Error rendering button ${index}:`, error);
                      return null;
                    }
                  })}
                </Box>
              )}

              {/* Message Status and Time */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '8px',
                  marginLeft: '20px'
                }}
              >
                <Typography sx={{ fontSize: '11px', color: '#8696a0' }}>
                  {(() => {
                    try {
                      return new Date().toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                    } catch (error) {
                      return new Date().toTimeString().slice(0, 5);
                    }
                  })()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#53bdeb' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l3.61 3.463c.143.14.361.125.484-.033L10.91 3.879a.366.366 0 0 0-.064-.512z" />
                  </svg>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } catch (error) {
      console.warn('Error rendering regular template:', error);
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#ff6b6b' }}>
          <Typography sx={{ fontSize: '14px' }}>Template preview unavailable</Typography>
        </Box>
      );
    }
  };

  // Safe carousel template rendering
  const renderCarouselTemplate = () => {
    try {
      const carouselSection = getParsedTemplate.find((section: any) =>
        section && typeof section === 'object' && section.type === 'CAROUSEL'
      );

      if (!carouselSection?.cards || !Array.isArray(carouselSection.cards) || carouselSection.cards.length === 0) {
        return (
          <Box sx={{ padding: '16px', textAlign: 'center' }}>
            <Typography color="error">Invalid carousel template structure</Typography>
          </Box>
        );
      }

      const cards = carouselSection.cards;
      const totalCards = cards.length;
      const safeCurrentIndex = Math.max(0, Math.min(currentCardIndex, totalCards - 1));

      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Box sx={{ maxWidth: '280px', position: 'relative' }}>
            {/* Carousel Navigation */}
            {totalCards > 1 && (
              <>
                <IconButton
                  onClick={() => {
                    try {
                      setCurrentCardIndex(prev => prev > 0 ? prev - 1 : totalCards - 1);
                    } catch (error) {
                      console.warn('Error navigating carousel:', error);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    left: '-12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)'
                    }
                  }}
                >
                  <ChevronLeft style={{ width: '18px', height: '18px' }} />
                </IconButton>

                <IconButton
                  onClick={() => {
                    try {
                      setCurrentCardIndex(prev => prev < totalCards - 1 ? prev + 1 : 0);
                    } catch (error) {
                      console.warn('Error navigating carousel:', error);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    right: '-12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)'
                    }
                  }}
                >
                  <ChevronRight style={{ width: '18px', height: '18px' }} />
                </IconButton>
              </>
            )}

            {/* Current Card */}
            {renderCarouselCard(cards[safeCurrentIndex], safeCurrentIndex)}

            {/* Card Indicators */}
            {totalCards > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '8px',
                  padding: '4px 8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '12px',
                  width: 'fit-content',
                  margin: '8px auto 0'
                }}
              >
                {cards.map((_: any, index: number) => (
                  <Box
                    key={index}
                    onClick={() => {
                      try {
                        setCurrentCardIndex(index);
                      } catch (error) {
                        console.warn('Error setting card index:', error);
                      }
                    }}
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: index === safeCurrentIndex ? '#25d366' : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Carousel Info */}
            <Box sx={{ textAlign: 'right', marginTop: '4px' }}>
              <Typography sx={{ fontSize: '11px', color: '#8696a0' }}>
                {totalCards > 1 && `${safeCurrentIndex + 1} of ${totalCards}`}
              </Typography>
            </Box>

            {/* Message Status and Time */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                marginLeft: '20px'
              }}
            >
              <Typography sx={{ fontSize: '11px', color: '#8696a0' }}>
                {(() => {
                  try {
                    return new Date().toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });
                  } catch (error) {
                    return new Date().toTimeString().slice(0, 5);
                  }
                })()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#53bdeb' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l3.61 3.463c.143.14.361.125.484-.033L10.91 3.879a.366.366 0 0 0-.064-.512z" />
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } catch (error) {
      console.warn('Error rendering carousel template:', error);
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#ff6b6b' }}>
          <Typography sx={{ fontSize: '14px' }}>Carousel preview unavailable</Typography>
        </Box>
      );
    }
  };

  // Main render with error boundary
  if (componentError) {
    return <ErrorFallback error={componentError} resetError={() => setComponentError(null)} />;
  }

  try {
    return (
      <Box sx={{ maxWidth: '320px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {/* WhatsApp UI Container */}
        <Box
          sx={{
            backgroundColor: '#0b141a',
            padding: '8px 16px 16px 16px',
            borderRadius: '12px',
            position: 'relative',
            minHeight: '500px'
          }}
        >
          {/* WhatsApp Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              padding: '8px 0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Box
                sx={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#128c7e',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                B
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#e9edef',
                    lineHeight: 1.2
                  }}
                >
                  Business
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#8696a0',
                    lineHeight: 1.2
                  }}
                >
                  Online
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Box sx={{ color: '#8696a0' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.9 14.3H15L14.7 14C15.8 12.8 16.3 11.1 16.3 9.3C16.3 5.8 13.4 3 9.9 3C6.4 3 3.5 5.9 3.5 9.4C3.5 12.9 6.4 15.8 9.9 15.8C11.7 15.8 13.4 15.3 14.6 14.2L14.9 14.5V15.3L19.2 19.6L20.6 18.2L15.9 14.3ZM9.9 14.3C7.2 14.3 5 12.1 5 9.4C5 6.7 7.2 4.5 9.9 4.5C12.6 4.5 14.8 6.7 14.8 9.4C14.8 12.1 12.6 14.3 9.9 14.3Z" />
                </svg>
              </Box>
              <Box sx={{ color: '#8696a0' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" />
                </svg>
              </Box>
            </Box>
          </Box>

          {/* Message Container */}
          <Box sx={{ position: 'relative', paddingBottom: '60px' }}>
            {isCarousel ? renderCarouselTemplate() : renderRegularTemplate()}
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '16px',
              left: '16px',
              right: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#2a3942',
              borderRadius: '24px',
              padding: '8px 16px'
            }}
          >
            <Typography
              sx={{
                flex: 1,
                color: '#8696a0',
                fontSize: '15px'
              }}
            >
              Type a message
            </Typography>
          </Box>
        </Box>

        {/* General Template Error */}
        {safeErrors?.template && (
          <Box
            sx={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: '8px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: '#d32f2f'
              }}
            >
              <AlertCircle style={{ width: '20px', height: '20px', marginRight: '8px' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{String(safeErrors.template)}</Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  } catch (error) {
    console.error('Critical error in WhatsAppPreview:', error);
    setComponentError(error as Error);
    return <ErrorFallback error={error as Error} resetError={() => setComponentError(null)} />;
  }
};

// Wrapped component with Error Boundary
const WhatsAppPreviewWithErrorBoundary = (props: PreviewProps) => (
  <WhatsAppPreviewErrorBoundary>
    <WhatsAppPreview {...props} />
  </WhatsAppPreviewErrorBoundary>
);

export default WhatsAppPreviewWithErrorBoundary;